import { Link } from "react-router-dom";
import { useState } from "react";
import { Check, ArrowRight, Leaf, Utensils, Users, Home as HomeIcon, Heart, Sparkles, Fish, Beef, Wheat, Gem, Cookie, Salad, ChevronDown, Flame, Globe } from "lucide-react";
import StockImage from "@/components/StockImage";

const REPERTOIRE = [
  { icon: Salad, title: "Vegetarisch", text: "Vollwertige, eigenständige Gerichte — nicht das ausgelassene Steak, sondern der Star des Menüs. Von deftig bis fein." },
  { icon: Leaf, title: "Vegan", text: "Cremig ohne Sahne, würzig ohne Käse — vegane Küche auf Sterne-Niveau, geplant von Grund auf, nicht nur ‚weggelassen'." },
  { icon: Fish, title: "Fisch & Meeresfrüchte", text: "Von der handgeräucherten Forelle bis zum ganzen Wolfsbarsch in Salzkruste. Frisch, tagesaktuell, punktgenau gegart." },
  { icon: Beef, title: "Fleisch & Wild", text: "Von der klassischen Roulade bis Dry-Aged-Beef. Verantwortungsvolle Herkunft, präzise Garung, ehrliche Aromen." },
  { icon: Flame, title: "BBQ & Grill", text: "Low & Slow, Steak-Kompetenz oder ganze Grill-Abende im Garten — vom Pulled Pork bis zum perfekten Tomahawk." },
  { icon: Globe, title: "International", text: "Italienisch, französisch, asiatisch, mediterran — ich hole für Ihren Abend die Aromen der Welt in Ihre Küche." },
  { icon: Wheat, title: "Allergene & Unverträglichkeiten", text: "Glutenfrei, laktosefrei, histaminarm, Nussallergie — jeder Wunsch wird ernst genommen, jede Zutat einzeln geprüft." },
  { icon: Gem, title: "Specials & Luxus", text: "Wagyu, Kaviar, Trüffel, Steinbutt, Hummer — wenn es besonders werden soll, plane ich mit den besten Zutaten der Saison." },
  { icon: Cookie, title: "Patisserie & Dessert", text: "Vom klassischen Soufflé bis zum modernen Signature-Dessert. Handgemachte Pralinen und Petit Fours auf Wunsch inklusive." },
  { icon: Sparkles, title: "Alle deine Wünsche", text: "Lieblingsgericht deiner Kindheit? Überraschungsmenü? Regionale Spezialität aus deiner Heimat? Sag es mir — ich koche es." },
];

const ANLAESSE = [
  { icon: Heart, title: "Dinner für zwei", text: "Ein Abend nur für Sie beide — ohne Reservierung, ohne Zeitdruck." },
  { icon: Sparkles, title: "Geburtstage", text: "Ein Menü, das Ihre Gäste in Erinnerung behalten — in Ihrem Ambiente." },
  { icon: Utensils, title: "Besondere Anlässe", text: "Jubiläen, Weihnachten, Silvester oder einfach ein schöner Abend mit Freunden." },
];

const ABLAUF = [
  {
    step: "01",
    title: "Persönliches Vorgespräch",
    text: "Wir treffen uns oder telefonieren — Sie erzählen mir vom Anlass, Ihren Wünschen, Vorlieben und Allergien Ihrer Gäste. Ich gestalte das Menü ganz nach Ihnen.",
  },
  {
    step: "02",
    title: "Menüplanung & Einkauf",
    text: "Ich stelle Ihr Menü nach Ihren Wünschen zusammen und besorge alle Zutaten frisch und saisonal. Sie müssen sich um nichts kümmern.",
  },
  {
    step: "03",
    title: "Der Abend bei Ihnen zuhause",
    text: "Ich koche in Ihrer Küche — auch in kleineren Küchen bin ich vorbereitet — serviere die Gänge am Tisch und bleibe bis zum Schluss.",
  },
  {
    step: "04",
    title: "Nach dem Essen",
    text: "Sie genießen den Abend, ich kümmere mich um alles — inklusive Abräumen und Aufräumen der Küche. Am Ende bleibt nur die Erinnerung.",
  },
];

// Bildunterschriften — Hero + zweites Menü-Bild (bitte bei Bedarf ersetzen)
const HERO_CAPTION = "Thunfisch mit Sesamkruste / Grünes Gazpacho / Erbse / Sesam-Eis";
const SECONDARY_CAPTION = "Jakobsmuscheln / Eingelegter Kürbis süß-sauer / Buchenpilze / Limonen-Kokos-Eis";

const GALLERY = [
  {
    course: "Vorspeise",
    title: "Thunfisch / Papaya / Avocado / Eingelegter Ingwer / Teriyaki",
    src: "https://customer-assets.emergentagent.com/job_lukas-mietkoch/artifacts/bk9layaq_Screenshot_20210830-221227_Chrome_Original.jpeg",
    alt: "Thunfisch mit Papaya, Avocado, eingelegtem Ingwer und Teriyaki",
  },
  {
    course: "Zwischengang",
    title: "Grüne-Soße-Ravioli / Parmesan-Schaum & Cracker / Konfierte Kirschtomaten",
    src: "https://customer-assets.emergentagent.com/job_lukas-mietkoch/artifacts/as9p94no_IMG-20190414-WA0002_Original.jpeg",
    alt: "Grüne-Soße-Ravioli mit Parmesan-Schaum, Parmesan-Cracker und konfierten Kirschtomaten",
  },
  {
    course: "Hauptgang",
    title: "Kaffee-gebeiztes Roastbeef / Perlgraupen-Risotto / Pfifferlinge / Wilder Brokkoli",
    src: "https://customer-assets.emergentagent.com/job_lukas-mietkoch/artifacts/l9nv5gyr_IMG_0669_Original.webp",
    alt: "Kaffee-gebeiztes Roastbeef mit Perlgraupen-Risotto, Pfifferlingen und wildem Brokkoli",
  },
  {
    course: "Dessert",
    title: "Schokoladenmousse / Mango / Himbeere / Pistazie",
    src: "/dessert-schokoladenmousse.jpg",
    alt: "Schokoladenmousse mit Mango, Himbeere und Pistazie",
  },
];

const HIGHLIGHTS = [
  "Kochen ganz nach Ihren Wünschen — 3 bis 7 Gänge",
  "Frische, saisonale Zutaten — von mir persönlich ausgewählt",
  "Persönliche Menü-Beratung im Vorgespräch",
  "Serviere Ihre Gäste am Tisch",
  "Ab 2 Personen — Dinner für zwei bis zur großen Runde",
  "In Deutschland und Österreich — Festpreis, alles inklusive",
];

export default function PrivatKoch() {
  const [showRepertoire, setShowRepertoire] = useState(false);
  return (
    <div data-testid="page-privatkoch">
      {/* Hero */}
      <section className="border-b border-[#E3DECE] relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-32 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          <div className="lg:col-span-6">
            <p className="text-xs uppercase tracking-[0.25em] font-semibold text-[#BA6D52]">Privatkoch für zuhause</p>
            <h1 className="font-serif text-4xl sm:text-5xl md:text-6xl font-light tracking-tighter mt-6">
              Ein Abend, ganz für Sie.
            </h1>
            <p className="text-[#757067] font-light text-lg mt-8 leading-relaxed">
              Feiern in den eigenen vier Wänden — ohne Hektik, ohne Sperrzeiten,
              ohne den Aufwand danach. Ich koche für Sie und Ihre Gäste ein Menü
              nach Ihren Wünschen. Sie genießen den Abend, ich kümmere mich um alles.
            </p>
            <div className="mt-10 flex flex-col sm:flex-row gap-4">
              <Link
                to="/anfrage-privat"
                data-testid="privatkoch-cta-primary"
                className="inline-flex items-center justify-center gap-3 bg-[#BA6D52] text-white hover:bg-[#9E5841] px-8 py-5 text-xs uppercase tracking-[0.2em] transition-colors"
              >
                Menü-Beratung anfragen <ArrowRight size={16} />
              </Link>
              <a
                href="#ablauf"
                data-testid="privatkoch-cta-secondary"
                onClick={(e) => {
                  e.preventDefault();
                  document.getElementById("ablauf")?.scrollIntoView({ behavior: "smooth", block: "start" });
                }}
                className="inline-flex items-center justify-center gap-3 border border-[#2C2A26] text-[#2C2A26] hover:bg-[#2C2A26] hover:text-white px-8 py-5 text-xs uppercase tracking-[0.2em] transition-colors"
              >
                So funktioniert es
              </a>
            </div>
          </div>
          <div className="lg:col-span-6">
            <figure>
              <StockImage
                aspect="aspect-[4/5]"
                src="https://customer-assets.emergentagent.com/job_lukas-mietkoch/artifacts/vvawpn9n_93F23D98-99F4-414C-A56E-5D743019B44A.png"
                alt="Signature-Gang aus meiner Küche"
                data-testid="privatkoch-hero-image"
              />
              <figcaption className="mt-4 pt-3 border-t border-[#BA6D52]/30" data-testid="privatkoch-hero-caption">
                <p className="text-[10px] uppercase tracking-[0.3em] text-[#BA6D52] font-semibold">Signature</p>
                <p className="font-serif text-base sm:text-lg italic text-[#2C2A26] mt-1 leading-relaxed break-words hyphens-auto">
                  {HERO_CAPTION.split(" / ").map((part, idx, arr) => (
                    <span key={idx}>
                      {part}
                      {idx < arr.length - 1 && <span className="text-[#BA6D52] mx-1.5">/</span>}
                    </span>
                  ))}
                </p>
              </figcaption>
            </figure>
          </div>
        </div>
      </section>

      {/* Warum Privatkoch */}
      <section className="border-b border-[#E3DECE] bg-[#F2EFE9]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-28">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            <div className="lg:col-span-4">
              <p className="text-xs uppercase tracking-[0.25em] font-semibold text-[#BA6D52]">Warum ich Sie zuhause bekoche</p>
              <h2 className="font-serif text-4xl sm:text-5xl font-light tracking-tighter mt-6">
                Weil manche Abende<br/>nach Ruhe verlangen.
              </h2>
            </div>
            <div className="lg:col-span-8">
              <div className="space-y-6 text-[#2C2A26] font-light text-lg leading-relaxed">
                <p>
                  Manche Anlässe fühlen sich zuhause einfach besser an. Kein
                  Restaurantlärm, keine fremden Blicke, keine Zeit, zu der die
                  Küche schließt. Ihre Gäste kommen wann sie wollen — und bleiben,
                  solange sie mögen.
                </p>
                <p>
                  Sie feiern in Ihrer vertrauten Umgebung, mit Ihrer Musik, mit
                  Ihren Menschen. Und trotzdem müssen Sie sich um nichts kümmern —
                  weder um das Menü, noch um den Einkauf, noch um das Aufräumen danach.
                </p>
                <p className="text-[#BA6D52] font-serif italic text-2xl leading-snug pt-4">
                  &bdquo;Sie sind Gast an Ihrem eigenen Abend — ich mache den Rest.&ldquo;
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Menü statt Catering — Klarstellung */}
      <section className="border-b border-[#E3DECE] bg-white" data-testid="privatkoch-menu-statt-catering">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-24">
          <div className="max-w-3xl">
            <p className="text-xs uppercase tracking-[0.25em] font-semibold text-[#BA6D52]">So arbeite ich</p>
            <h2 className="font-serif text-4xl sm:text-5xl font-light tracking-tighter mt-6">
              Menü-Abend, <span className="italic text-[#BA6D52]">kein Catering.</span>
            </h2>
            <p className="text-[#757067] font-light text-lg mt-6 leading-relaxed">
              Ein wichtiger Unterschied — damit wir beide vom gleichen Abend reden.
              Als Privatkoch koche ich <strong className="text-[#2C2A26] font-normal">frisch bei Ihnen zuhause</strong>{" "}
              und serviere die Gänge <strong className="text-[#2C2A26] font-normal">am gedeckten Tisch</strong>.
              Es geht um den einen Abend, nicht um Massenverpflegung.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12 mt-14">
            {/* Was ich anbiete */}
            <div className="border-l-2 border-[#BA6D52] pl-6 md:pl-8" data-testid="privatkoch-was-ich-anbiete">
              <p className="text-xs uppercase tracking-[0.25em] font-semibold text-[#BA6D52]">Das mache ich</p>
              <h3 className="font-serif text-2xl mt-3 text-[#2C2A26] font-light">Ein Menü bei Ihnen zuhause.</h3>
              <ul className="mt-6 space-y-3 text-[#2C2A26] font-light">
                {[
                  "Menü-Abende mit 3–7 Gängen, à la carte serviert",
                  "In Ihrer vorhandenen Küche gekocht",
                  "Am gedeckten Tisch, Gang für Gang",
                  "Ab 2 Personen — größere Runden je nach Location auf Anfrage",
                  "Ich bleibe bis zum letzten Kaffee",
                ].map((t) => (
                  <li key={t} className="flex items-start gap-3">
                    <Check size={18} className="text-[#BA6D52] mt-1 shrink-0" />
                    <span>{t}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Was ich NICHT anbiete */}
            <div className="border-l-2 border-[#C7BFB0] pl-6 md:pl-8" data-testid="privatkoch-was-ich-nicht-anbiete">
              <p className="text-xs uppercase tracking-[0.25em] font-semibold text-[#8A7A68]">Das mache ich nicht</p>
              <h3 className="font-serif text-2xl mt-3 text-[#2C2A26] font-light">Kein mobiles Catering.</h3>
              <ul className="mt-6 space-y-3 text-[#757067] font-light">
                {[
                  "Kein Buffet oder Brunch mit Chafing Dishes / Warmhaltebehältern",
                  "Keine Locations ohne funktionierende Küche vor Ort",
                  "Keine Selbstbedienungs-Events oder Stehempfänge",
                  "Keine Barbetreuung oder Kellner-Team",
                  "Keine Großveranstaltungen ab 16 Personen",
                ].map((t) => (
                  <li key={t} className="flex items-start gap-3">
                    <span className="text-[#C7BFB0] mt-1 shrink-0 font-serif italic">—</span>
                    <span>{t}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div className="mt-14 pt-8 border-t border-[#E3DECE] max-w-3xl">
            <p className="text-sm text-[#757067] font-light italic leading-relaxed">
              Sie planen ein Buffet, einen Brunch oder ein Catering mit Ausgabe vor Ort?
              Schreiben Sie mir trotzdem gern — je nach Location und Küche geht auch eine
              große Runde, sonst empfehle ich Ihnen einen passenden Kollegen aus meinem
              Netzwerk.{" "}
              <Link to="/kontakt" className="text-[#BA6D52] underline underline-offset-4 hover:text-[#9E5841]">
                Zum Kontakt
              </Link>.
            </p>
          </div>
        </div>
      </section>

      {/* Anlässe */}
      <section className="border-b border-[#E3DECE]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-28">
          <p className="text-xs uppercase tracking-[0.25em] font-semibold text-[#BA6D52]">Für welche Anlässe</p>
          <h2 className="font-serif text-4xl sm:text-5xl font-light tracking-tighter mt-6 max-w-2xl">
            Vom Dinner für zwei bis zur großen Tafel.
          </h2>
          <p className="text-[#757067] font-light mt-5 max-w-2xl leading-relaxed">
            Ab zwei Personen — nach oben gibt es keine feste Grenze: Je nach Location und
            Gegebenheiten sind auch deutlich mehr als 15 Gäste möglich. Sprechen Sie mich
            einfach an, dann schauen wir uns Küche und Ablauf gemeinsam an.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-14">
            {ANLAESSE.map((a) => {
              const Icon = a.icon;
              return (
                <div key={a.title} className="border border-[#E3DECE] p-8 hover:border-[#BA6D52] transition-colors">
                  <Icon size={32} className="text-[#BA6D52]" strokeWidth={1.4} />
                  <h3 className="font-serif text-2xl mt-6 text-[#2C2A26] font-light">{a.title}</h3>
                  <p className="text-[#757067] font-light mt-3 leading-relaxed">{a.text}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Ablauf */}
      <section id="ablauf" className="border-b border-[#E3DECE] bg-[#F2EFE9]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-28">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
            <div className="lg:col-span-4 lg:sticky lg:top-32">
              <p className="text-xs uppercase tracking-[0.25em] font-semibold text-[#BA6D52]">So funktioniert es</p>
              <h2 className="font-serif text-4xl sm:text-5xl font-light tracking-tighter mt-6">
                Vier Schritte<br/>zu Ihrem Abend.
              </h2>
              <p className="text-[#757067] font-light mt-6 leading-relaxed">
                Von der ersten Idee bis zum abgeräumten Tisch — Sie geben nur den
                Anlass und die Wünsche, alles andere übernehme ich.
              </p>
            </div>
            <div className="lg:col-span-8 space-y-8">
              {ABLAUF.map((a) => (
                <div key={a.step} className="grid grid-cols-12 gap-6 items-start border-l-2 border-[#BA6D52] pl-6 py-2">
                  <div className="col-span-2 md:col-span-1">
                    <span className="font-serif text-3xl text-[#BA6D52] font-light">{a.step}</span>
                  </div>
                  <div className="col-span-10 md:col-span-11">
                    <h3 className="font-serif text-2xl text-[#2C2A26] font-light">{a.title}</h3>
                    <p className="text-[#757067] font-light mt-2 leading-relaxed">{a.text}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Was inklusive ist */}
      <section className="border-b border-[#E3DECE]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-28 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          <div className="lg:col-span-6 order-2 lg:order-1">
            <p className="text-xs uppercase tracking-[0.25em] font-semibold text-[#BA6D52]">Rundum-Service</p>
            <h2 className="font-serif text-4xl sm:text-5xl font-light tracking-tighter mt-6">
              Was in Ihrem Menü<br/>enthalten ist.
            </h2>
            <ul className="mt-10 space-y-4">
              {HIGHLIGHTS.map((h) => (
                <li key={h} className="flex items-start gap-3 text-[#2C2A26]">
                  <Check size={20} className="text-[#BA6D52] mt-1 shrink-0" />
                  <span className="font-light text-lg">{h}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="lg:col-span-6 order-1 lg:order-2">
            <figure>
              <StockImage
                aspect="aspect-[4/5]"
                src="https://customer-assets.emergentagent.com/job_lukas-mietkoch/artifacts/u5cbvlwh_IMG_3895.JPG.jpeg"
                alt="Menü-Impression aus meiner Küche"
                data-testid="privatkoch-menu-image"
              />
              <figcaption className="mt-4 pt-3 border-t border-[#BA6D52]/30" data-testid="privatkoch-menu-caption">
                <p className="text-[10px] uppercase tracking-[0.3em] text-[#BA6D52] font-semibold">Menü-Beispiel</p>
                <p className="font-serif text-base sm:text-lg italic text-[#2C2A26] mt-1 leading-relaxed break-words hyphens-auto">
                  {SECONDARY_CAPTION.split(" / ").map((part, idx, arr) => (
                    <span key={idx}>
                      {part}
                      {idx < arr.length - 1 && <span className="text-[#BA6D52] mx-1.5">/</span>}
                    </span>
                  ))}
                </p>
              </figcaption>
            </figure>
          </div>
        </div>
      </section>

      {/* Küchen-Repertoire — Was ich abdecke */}
      <section className="border-b border-[#E3DECE] bg-white">
        <div className="max-w-6xl mx-auto px-6 md:px-12 py-20 md:py-28">
          <div className="max-w-2xl mb-10">
            <p className="text-xs uppercase tracking-[0.25em] font-semibold text-[#BA6D52]">Meine Küche kann</p>
            <h2 className="font-serif text-4xl sm:text-5xl font-light tracking-tighter mt-6">
              Auf jeden Wunsch<br/>
              <span className="italic text-[#BA6D52]">gehe ich ein.</span>
            </h2>
            <p className="mt-6 text-lg font-light text-[#2C2A26] leading-relaxed">
              Vom klassischen Braten bis zum veganen Fine-Dining-Menü, von der Allergikerküche bis zum Luxus-Menü mit Kaviar und Wagyu. Was du dir vorstellst — ich setze es um.
            </p>
          </div>

          <button
            type="button"
            onClick={() => setShowRepertoire((v) => !v)}
            data-testid="privatkoch-repertoire-toggle"
            className="inline-flex items-center gap-3 border-2 border-[#BA6D52] text-[#BA6D52] hover:bg-[#BA6D52] hover:text-white px-8 py-4 font-serif text-lg transition-colors"
          >
            {showRepertoire ? "Ausblenden" : "Küchen-Repertoire anzeigen"}
            <ChevronDown size={22} className={`transition-transform ${showRepertoire ? "rotate-180" : ""}`} />
          </button>

          {showRepertoire && (
            <div
              data-testid="privatkoch-repertoire-grid"
              className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6"
            >
              {REPERTOIRE.map((r) => {
                const Icon = r.icon;
                return (
                  <div
                    key={r.title}
                    className="group border border-[#E3DECE] bg-[#FAFAF7] hover:border-[#BA6D52] hover:bg-white transition-all p-6"
                  >
                    <div className="w-11 h-11 flex items-center justify-center border border-[#BA6D52]/40 group-hover:border-[#BA6D52] group-hover:bg-[#BA6D52] transition-colors">
                      <Icon size={20} className="text-[#BA6D52] group-hover:text-white transition-colors" />
                    </div>
                    <h3 className="font-serif text-xl mt-5 text-[#2C2A26]">{r.title}</h3>
                    <p className="mt-3 text-sm text-[#2C2A26]/80 font-light leading-relaxed">{r.text}</p>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* Menü-Galerie */}
      <section className="border-b border-[#E3DECE] bg-[#F2EFE9]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-28">
          <div className="max-w-2xl">
            <p className="text-xs uppercase tracking-[0.25em] font-semibold text-[#BA6D52]">Aus meiner Küche</p>
            <h2 className="font-serif text-4xl sm:text-5xl font-light tracking-tighter mt-6">
              Ein Auszug aus<br/>meinen Kreationen.
            </h2>
            <p className="text-[#757067] font-light text-lg mt-6 leading-relaxed">
              Ihr Menü stelle ich nach unserem Gespräch ganz individuell zusammen —
              hier ein Auszug aus früheren Menüs, für einen ersten Eindruck von
              Handschrift und Anrichten.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8 mt-14" data-testid="privatkoch-gallery">
            {GALLERY.map((g) => (
              <figure key={g.src} className="group flex flex-col">
                <div className="mb-4 pb-3 border-b border-[#BA6D52]/30 lg:min-h-[7rem] lg:flex lg:flex-col lg:justify-start">
                  <p className="text-[10px] uppercase tracking-[0.3em] text-[#BA6D52] font-semibold">{g.course}</p>
                  <p className="font-serif text-lg md:text-xl italic text-[#2C2A26] mt-1.5 leading-snug">
                    {g.title}
                  </p>
                </div>
                <StockImage aspect="aspect-[3/4]" src={g.src} alt={g.alt} />
              </figure>
            ))}
          </div>
        </div>
      </section>

      {/* Preise */}
      <section className="border-b border-[#E3DECE] bg-[#2C2A26] text-[#FAFAF7]">
        <div className="max-w-4xl mx-auto px-6 md:px-12 py-20 md:py-28 text-center">
          <p className="text-xs uppercase tracking-[0.25em] text-[#BA6D52] font-semibold">Investition</p>
          <h2 className="font-serif text-4xl sm:text-5xl font-light tracking-tighter mt-6">
            Ihr Menü — Ihr Preis.
          </h2>
          <p className="text-[#FAFAF7]/80 font-light text-lg mt-8 leading-relaxed max-w-2xl mx-auto">
            Jeder Abend ist anders. Deshalb kalkuliere ich Ihr Menü individuell —
            abhängig von Anzahl der Gänge, gewählten Zutaten und Anzahl
            Ihrer Gäste. Nach unserem Gespräch erhalten Sie ein transparentes
            Angebot mit allen Positionen.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-14 text-left">
            <div className="border border-[#FAFAF7]/20 p-6">
              <Utensils size={24} className="text-[#BA6D52]" strokeWidth={1.4} />
              <p className="font-serif text-xl mt-4">3–7 Gänge</p>
              <p className="text-sm text-[#FAFAF7]/60 mt-2 font-light">Ganz nach Anlass und Ihren Wünschen</p>
            </div>
            <div className="border border-[#FAFAF7]/20 p-6">
              <Leaf size={24} className="text-[#BA6D52]" strokeWidth={1.4} />
              <p className="font-serif text-xl mt-4">Frisch &amp; saisonal</p>
              <p className="text-sm text-[#FAFAF7]/60 mt-2 font-light">Zutaten von mir persönlich ausgewählt</p>
            </div>
            <div className="border border-[#FAFAF7]/20 p-6">
              <Users size={24} className="text-[#BA6D52]" strokeWidth={1.4} />
              <p className="font-serif text-xl mt-4">Ab 2 Gästen</p>
              <p className="text-sm text-[#FAFAF7]/60 mt-2 font-light">Intimes Dinner oder große Runde — auf Anfrage</p>
            </div>
          </div>
          <Link
            to="/anfrage-privat"
            data-testid="privatkoch-cta-preise"
            className="mt-14 inline-flex items-center gap-3 bg-[#BA6D52] text-white hover:bg-[#9E5841] px-8 py-5 text-xs uppercase tracking-[0.2em] transition-colors"
          >
            Kostenlose Menü-Beratung anfragen <ArrowRight size={16} />
          </Link>
        </div>
      </section>

      {/* Final CTA */}
      <section>
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-28 text-center">
          <HomeIcon size={40} className="text-[#BA6D52] mx-auto" strokeWidth={1.2} />
          <h2 className="font-serif text-4xl sm:text-5xl font-light tracking-tighter mt-8 max-w-3xl mx-auto">
            Ihr Abend — meine Leidenschaft.
          </h2>
          <p className="text-[#757067] font-light text-lg mt-6 max-w-2xl mx-auto">
            Schreiben Sie mir Ihren Anlass und Ihre Vorstellungen. Ich melde mich
            innerhalb von 24 Stunden für ein persönliches Vorgespräch.
          </p>
          <Link
            to="/anfrage-privat"
            data-testid="privatkoch-cta-final"
            className="mt-10 inline-flex items-center gap-3 bg-[#BA6D52] text-white hover:bg-[#9E5841] px-8 py-5 text-xs uppercase tracking-[0.2em] transition-colors"
          >
            Jetzt anfragen <ArrowRight size={16} />
          </Link>
        </div>
      </section>
    </div>
  );
}
